import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, LogOut, Globe, ChevronDown } from "lucide-react";
import { useTranslation } from 'react-i18next';

/* Selected-language chip, tinted with the shared admin accent so this menu
   looks right on the gold (accounting) admin as well as the blue one. */
const ACTIVE_LANG = {
    background: 'var(--brand-soft)',
    borderColor: 'var(--brand-line)',
    color: 'var(--brand-d)',
} as const;

export default function AdminProfileMenu() {
    const navigate = useNavigate();
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [adminData, setAdminData] = useState<{ email?: string } | null>(null);
    const { t, i18n } = useTranslation();
    const [currentLang, setCurrentLang] = useState(i18n.language);
    const isRTL = i18n.language === 'ar';

    useEffect(() => {
        const adminUserStr = localStorage.getItem('adminUser');
        if (adminUserStr) {
            try {
                const adminUser = JSON.parse(adminUserStr);
                setAdminData(adminUser);
            } catch (err) {
                console.error('Failed to parse adminUser', err);
            }
        }
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('authTokens');
        navigate('/admin-login');
    };

    const changeLanguage = (lang: string) => {
        i18n.changeLanguage(lang);
        setCurrentLang(lang);
        document.dir = lang === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = lang;
        setShowProfileMenu(false);
    };

    const adminName = adminData?.email || 'Admin';

    return (
        <div className="relative">
            <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className={`flex items-center gap-2 md:gap-3 ml-2 md:ml-6 hover:bg-gray-50 px-2 py-1 rounded-lg transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
            >
                <ChevronDown className={`w-4 h-4 text-slate-400`} />
                <div className={`flex flex-col ${isRTL ? 'items-start' : 'items-end'} hidden sm:flex`}>
                    <span className="text-xs md:text-sm font-semibold text-slate-700 truncate max-w-[100px] sm:max-w-[150px] md:max-w-none">
                        {adminName}
                    </span>
                </div>
                {/* Brand accent, not a hardcoded blue — this bar renders on the
                    accounting (gold) admin too. */}
                <div
                    className="w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center flex-shrink-0 border"
                    style={{ background: 'var(--brand-soft)', borderColor: 'var(--brand-line)' }}
                >
                    <User className="w-4 h-4 md:w-[18px] md:h-[18px]" style={{ color: 'var(--brand-d)' }} />
                </div>
            </button>

            {showProfileMenu && (
                <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50 animate-in fade-in zoom-in-95 duration-100`}>
                    <div className="px-4 py-3 border-b border-gray-100 mb-1">
                        <p className="text-sm font-medium text-gray-900">Administrator</p>
                        <p className="text-xs text-gray-500 truncate">{adminName}</p>
                    </div>

                    {/* Language Selector */}
                    <div className="px-4 py-2 border-b border-gray-100">
                        <div className="flex items-center gap-2 mb-2">
                            <Globe className="w-3 h-3 text-gray-500" />
                            <p className="text-xs font-semibold text-gray-500 uppercase">Language</p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => changeLanguage('en')}
                                className={`flex-1 px-2 py-1 text-xs rounded border transition-colors ${currentLang === 'en' ? 'font-semibold' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                                style={currentLang === 'en' ? ACTIVE_LANG : undefined}
                            >
                                English
                            </button>
                            <button
                                onClick={() => changeLanguage('ar')}
                                className={`flex-1 px-2 py-1 text-xs rounded border transition-colors ${currentLang === 'ar' ? 'font-semibold' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                                style={currentLang === 'ar' ? ACTIVE_LANG : undefined}
                            >
                                العربية
                            </button>
                        </div>
                    </div>

                    <button
                        onClick={handleLogout}
                        className={`w-full ${isRTL ? 'text-right flex-row-reverse' : 'text-left'} px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2`}
                    >
                        <LogOut className="w-4 h-4" />
                        {t('adminSidebar.logout')}
                    </button>
                </div>
            )}
        </div>
    );
}
